import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { articles, getArticleById } from "@/mocks/articles";

const BASE = "https://mynami.cn";

export default function ArticleDetail() {
  const { id = "" } = useParams();
  const article = getArticleById(id);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const value = total > 0 ? (window.scrollY / total) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, value)));
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const headings = useMemo(
    () => (article ? article.content.filter((b) => b.type === "h2" && b.text).map((b) => b.text as string) : []),
    [article],
  );

  if (!article) {
    return (
      <SiteLayout>
        <Seo
          title="文章不存在 | 黑料每日"
          description="你访问的内容不存在或已被移除，可以回到首页继续浏览每日早报与每日一瓜。"
          canonicalPath="/article"
        />
        <div className="px-4 md:px-8 py-24 text-center">
          <i className="ri-file-unknow-line text-4xl text-foreground-400" aria-hidden="true" />
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mt-4">内容不存在</h1>
          <p className="text-sm text-foreground-600 mt-2">这篇文章可能已被移除，或链接有误。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 px-5 py-3 text-sm hover:bg-primary-600 transition-colors"
          >
            <i className="ri-home-4-line" aria-hidden="true" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const sectionName = article.kind === "gossip" ? "每日一瓜" : "每日早报";
  const sectionPath = article.kind === "gossip" ? "/gossip" : "/morning";
  const related = articles
    .filter((item) => item.id !== article.id && item.category === article.category)
    .slice(0, 3);
  const fallback = articles.filter((item) => item.id !== article.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallback;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.title,
      description: article.excerpt,
      image: [article.cover],
      datePublished: article.date,
      dateModified: article.date,
      articleSection: article.category,
      keywords: article.tags.join(","),
      inLanguage: "zh-CN",
      author: { "@type": "Person", name: article.author },
      publisher: {
        "@type": "Organization",
        name: "黑料每日",
        url: `${BASE}/`,
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${BASE}/article/${article.id}` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: sectionName, item: `${BASE}${sectionPath}` },
        { "@type": "ListItem", position: 3, name: article.title, item: `${BASE}/article/${article.id}` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${article.title} | 黑料每日`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalPath={`/article/${article.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />

      <div className="fixed top-0 left-0 right-0 z-[60] h-0.5 bg-transparent" aria-hidden="true">
        <div className="h-full bg-primary-500 transition-[width] duration-150" style={{ width: `${progress}%` }} />
      </div>

      <div className="px-4 md:px-8">
        <Breadcrumbs items={[{ label: sectionName, path: sectionPath }, { label: article.title }]} />
      </div>

      <article className="px-4 md:px-8">
        <header className="max-w-3xl mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Link
              to={`${sectionPath}?category=${encodeURIComponent(article.category)}`}
              className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors"
            >
              {article.category}
            </Link>
            <span className="text-xs text-foreground-500">{sectionName}</span>
            <time className="text-xs text-foreground-400" dateTime={article.date}>
              {article.date}
            </time>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-5">
            {article.title}
          </h1>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed">{article.excerpt}</p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
              约 {article.readTime}
            </span>
          </div>
        </header>

        <figure className="max-w-4xl mb-10">
          <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={article.cover}
              alt={`${article.title} 配图`}
              title={`${article.title} - ${article.category}`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <figcaption className="mt-2 text-xs text-foreground-400">{article.title} · 配图示意</figcaption>
        </figure>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="prose-readdy max-w-none">
              {article.content.map((block, index) => {
                if (block.type === "h2") {
                  return (
                    <h2
                      key={index}
                      id={`section-${index}`}
                      className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-8 mb-4 scroll-mt-24"
                    >
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === "h3") {
                  return (
                    <h3 key={index} className="font-heading text-lg font-semibold text-foreground-900 mt-6 mb-3">
                      {block.text}
                    </h3>
                  );
                }
                if (block.type === "quote") {
                  return (
                    <blockquote
                      key={index}
                      className="my-6 border-l-2 border-primary-500 bg-background-100 px-5 py-4 rounded-r-md font-heading text-base md:text-lg text-foreground-800 leading-relaxed"
                    >
                      {block.text}
                    </blockquote>
                  );
                }
                if (block.type === "list") {
                  return (
                    <ul key={index} className="my-5 flex flex-col gap-2">
                      {(block.items ?? []).map((item) => (
                        <li key={item} className="flex items-start gap-3 text-base text-foreground-700 leading-relaxed">
                          <i className="ri-checkbox-blank-circle-fill text-[6px] text-accent-500 mt-2.5" aria-hidden="true" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  );
                }
                return (
                  <p key={index} className="text-base text-foreground-700 leading-loose mb-5">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500 mr-1">标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/gossip?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1.5 rounded-full border border-background-300 text-xs text-foreground-600 hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200 flex flex-wrap items-center justify-between gap-4">
              <Link to={sectionPath} className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors">
                <i className="ri-arrow-left-line" aria-hidden="true" />
                返回{sectionName}
              </Link>
              <a
                href={`mailto:?subject=${encodeURIComponent(article.title)}`}
                rel="nofollow"
                className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
              >
                <i className="ri-mail-line" aria-hidden="true" />
                分享这篇文章
              </a>
            </div>
          </div>

          <aside className="lg:col-span-4" aria-label="文章目录与推荐">
            {headings.length > 0 && (
              <nav className="lg:sticky lg:top-24 rounded-lg border border-background-200 bg-background-100 p-5 mb-6" aria-label="文章目录">
                <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-list-check-2 text-primary-500" aria-hidden="true" />
                  本文目录
                </h2>
                <ul className="flex flex-col gap-2">
                  {headings.map((heading, index) => (
                    <li key={heading}>
                      <a
                        href={`#section-${index}`}
                        className="text-sm text-foreground-600 hover:text-primary-600 transition-colors block leading-snug"
                      >
                        {heading}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">相关推荐</h2>
              <div>
                {relatedList.map((item) => (
                  <ArticleCard key={item.id} article={item} variant="compact" />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </article>

      <section className="px-4 md:px-8 mt-16" aria-label="更多内容">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
          继续浏览{sectionName}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {relatedList.map((item) => (
            <ArticleCard key={item.id} article={item} variant="standard" />
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}